import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/react-dom';
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';
import { SelectorIcon as ChevronUpDownIcon } from '@heroicons/react/solid';
import classNames from 'classnames';
import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useMutation } from 'urql';
import { graphql } from '~/gql';
import { Tag as InputTagProps, TagFieldsFragment as Tag } from '~/gql/graphql';
import { useCheckOverflow } from '~/hooks/useCheckOverflow';
import { useScrollToFocus } from '~/hooks/useScrollToFocus';
import { PopoverStateChangedEffect } from '../PopoverStateChangedEffect';
import { Label } from '../ui/Label';
import { MenuTransition } from '../ui/Transition/MenuTransition';
import { CategoryRenameDialog } from './CategoryRenameDialog';
import { DeleteConfirmation } from './DeleteConfirmation';
import { TagCombolist } from './TagCombolist';
import { TagForm, TagFormSubmitResult } from './TagForm';
import { TagsBuilder } from './TagsBuilder';

export enum TagEntityType {
  Activity = 'Activity',
  Comment = 'Comment',
  Job = 'Job',
  Task = 'Task',
}

export const TagEntityTypes = [
  TagEntityType.Activity,
  TagEntityType.Comment,
  TagEntityType.Job,
  TagEntityType.Task,
];

type Props = {
  entityType: TagEntityType;
  options: Tag[];
  value: Tag[];
  customButton?: React.ReactNode;
  containerStyles?: string;
  onChange: (tags: Tag[], mode?: 'create') => void;

  /** `true` to manage open state externally */
  dialogMode?: boolean;
  /** Used in DialogMode */
  isOpen?: boolean;
  /** Used in DialogMode */
  hasPreview?: boolean;
  /** Used in DialogMode */
  onClose?: () => void;
  /** Used in DialogMode */
  onSubmit?: () => void;
  /** Used in DialogMode if true, displays label  */
  hasLabel?: boolean;
};

const TagFieldsFragment = graphql(`
  fragment TagFields on Tag {
    id
    entityType
    name
    category
    description
    colour
  }
`);

const CreateTagMutationDocument = graphql(`
  mutation CreateTag($input: CreateTagInput!) {
    createTag(input: $input) {
      ...TagFields
    }
  }
`);

const UpdateTagMutationDocument = graphql(`
  mutation UpdateTag($input: UpdateTagInput!) {
    updateTag(input: $input) {
      ...TagFields
    }
  }
`);

const DeleteTagDocument = graphql(`
  mutation DeleteTag($input: DeleteTagInput!) {
    deleteTag(input: $input)
  }
`);

// TODO this component needs to be refactored
export const TagCombobox = ({
  entityType,
  options,
  value,
  customButton,
  containerStyles,
  onChange,
  dialogMode = false,
  isOpen,
  hasPreview,
  onClose,
  onSubmit,
  hasLabel = true,
}: Props) => {
  const [formValues, setFormValues] = useState<Tag>();
  const [tagToDelete, setTagToDelete] = useState<Tag | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [categoryToRename, setCategoryToRename] = useState<string | null>(null);
  const ref = useScrollToFocus();

  const [, createTag] = useMutation(CreateTagMutationDocument);
  const [, updateTag] = useMutation(UpdateTagMutationDocument);
  const [, deleteTag] = useMutation(DeleteTagDocument);

  // Get unique categories from all tags
  const existingCategories = Array.from(
    new Set(options.filter((t) => t.category).map((t) => t.category!))
  );

  function removeFromSelected(id: string) {
    onChange([...value].filter((tag) => tag.id !== id));
  }

  const handleCreateTag = async (tag: Tag): Promise<TagFormSubmitResult> => {
    const isEdit = !!tag.id;

    if (isEdit) {
      const result = await updateTag({
        input: {
          id: tag.id,
          name: tag.name,
          category: tag.category,
          description: tag.description,
          colour: tag.colour,
        },
      });

      if (result.error) {
        const errorMessage =
          result.error.graphQLErrors?.[0]?.message ||
          result.error.message ||
          'Failed to update tag';
        return { success: false, error: errorMessage, field: 'name' };
      }

      const data = result.data as { updateTag: Tag };
      const updated = data.updateTag;
      // Only update the selection if the tag was already selected; don't append
      if (value.some((v) => v.id === updated.id)) {
        onChange(value.map((v) => (v.id === updated.id ? updated : v)));
      }
      toast.success('Tag successfully updated');
      setIsFormOpen(false);
      return { success: true };
    }

    tag.entityType = entityType;

    const result = await createTag({ input: tag });

    if (result.error) {
      const errorMessage =
        result.error.graphQLErrors?.[0]?.message ||
        result.error.message ||
        'Failed to create tag';
      return { success: false, error: errorMessage, field: 'name' };
    }

    const data = result.data as { createTag: Tag };
    onChange([...value, data.createTag], 'create');
    toast.success('Tag successfully created');
    setIsFormOpen(false);
    return { success: true };
  };

  const handleDeleteTag = async () => {
    if (!tagToDelete) return;

    const result = await deleteTag({
      input: { id: tagToDelete.id },
    });

    if (result?.error) {
      toast.error(result.error.graphQLErrors.join(', '));
    } else {
      toast.success('Tag successfully deleted');
    }

    setTagToDelete(null);
  };

  const handleRenameCategory = async (oldName: string, newName: string) => {
    // Find all tags with the old category name
    const tagsToUpdate = options.filter((tag) => tag.category === oldName);

    if (tagsToUpdate.length === 0) return;

    let successCount = 0;
    let errorCount = 0;

    for (const tag of tagsToUpdate) {
      const result = await updateTag({
        input: {
          id: tag.id,
          name: tag.name,
          category: newName,
          description: tag.description,
          colour: tag.colour,
        },
      });

      if (result.error) {
        errorCount++;
      } else {
        successCount++;
      }
    }

    if (errorCount > 0) {
      toast.error(`Failed to update ${errorCount} tag(s)`);
    }
    if (successCount > 0) {
      toast.success(
        `Category renamed: ${successCount} tag(s) updated to "${newName}"`
      );
    }
  };

  const handleDeleteCategory = async (categoryName: string) => {
    // Find all tags with this category
    const tagsToUpdate = options.filter((tag) => tag.category === categoryName);

    if (tagsToUpdate.length === 0) return;

    let successCount = 0;
    let errorCount = 0;

    for (const tag of tagsToUpdate) {
      const result = await updateTag({
        input: {
          id: tag.id,
          name: tag.name,
          category: null,
          description: tag.description,
          colour: tag.colour,
        },
      });

      if (result.error) {
        errorCount++;
      } else {
        successCount++;
      }
    }

    if (errorCount > 0) {
      toast.error(`Failed to update ${errorCount} tag(s)`);
    }
    if (successCount > 0) {
      toast.success(
        `Category "${categoryName}" deleted: ${successCount} tag(s) are now uncategorized`
      );
    }
  };

  return (
    <div ref={dialogMode ? null : ref} className='relative'>
      <TagForm
        initialValues={formValues}
        tags={options}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleCreateTag}
        isOpen={isFormOpen}
        onRenameCategory={setCategoryToRename}
        onDeleteCategory={handleDeleteCategory}
      />

      <CategoryRenameDialog
        isOpen={!!categoryToRename}
        categoryName={categoryToRename ?? ''}
        existingCategories={existingCategories}
        onClose={() => setCategoryToRename(null)}
        onSubmit={handleRenameCategory}
      />

      {dialogMode && isOpen ? (
        <DialogMode
          options={options}
          value={value}
          customButton={customButton}
          containerStyles={containerStyles}
          hasPreview={hasPreview}
          onRemove={removeFromSelected}
          onChange={onChange}
          onCreate={(tag) => {
            setFormValues(tag);
            setIsFormOpen(true);
          }}
          onDelete={setTagToDelete}
          onCloseDialog={onClose}
          onSubmit={onSubmit}
          hasLabel={hasLabel}
        />
      ) : !dialogMode ? (
        <PopoverMode
          options={options}
          customButton={customButton}
          containerStyles={containerStyles}
          value={value}
          onRemove={removeFromSelected}
          onChange={onChange}
          onCreate={(tag) => {
            setFormValues(tag);
            setIsFormOpen(true);
          }}
          onDelete={setTagToDelete}
          onModalOpen={setIsFormOpen}
          hasLabel={hasLabel}
        />
      ) : null}

      <DeleteConfirmation
        tag={tagToDelete}
        onClose={() => setTagToDelete(null)}
        onDelete={handleDeleteTag}
      />
    </div>
  );
};

type ModesProps = {
  options: Tag[];
  value: Tag[];
  customButton?: React.ReactNode;
  containerStyles?: string;
  hasPreview?: boolean;
  onModalOpen?: (v: boolean) => void;
  onChange: (tags: Tag[]) => void;
  onCreate: (tag?: InputTagProps | undefined) => void;
  onDelete: (tag: InputTagProps) => void;
  onRemove: (id: string) => void;
  onCloseDialog?: () => void;
  onSubmit?: () => void;
  hasLabel: boolean;
};

function PopoverMode({
  options,
  customButton,
  containerStyles,
  value,
  onModalOpen,
  onChange,
  onCreate,
  onDelete,
  onRemove,
  hasLabel,
}: ModesProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Floating UI configuration - matches TemplateSelect.tsx pattern exactly
  const { refs, floatingStyles } = useFloating({
    open: isOpen,
    placement: 'bottom-start',
    strategy: 'absolute',
    middleware: [
      offset(6),
      size({
        apply({ availableHeight, elements, rects }) {
          // Match reference width and set maxHeight to available space minus fixed button
          const adjustedHeight = Math.max(0, availableHeight - 96); // Account for fixed button
          const minHeight = 250; // Minimum acceptable height

          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            // If we have less than minHeight, set maxHeight to minHeight to trigger flip
            // Otherwise use the available space
            maxHeight:
              availableHeight < minHeight
                ? `${minHeight}px`
                : `${adjustedHeight}px`,
          });
        },
        padding: 6,
      }),
      flip({
        fallbackStrategy: 'initialPlacement',
        fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
        padding: 6,
      }),
      shift({ padding: 6 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  return (
    <>
      {!customButton && hasLabel && (
        <Label className='mb-1.5 ml-1 text-sm font-medium text-grey-50'>
          Tags
        </Label>
      )}

      <Popover ref={refs.setReference} className='relative'>
        {({ open }) => (
          <>
            {customButton ? (
              <PopoverButton>{customButton}</PopoverButton>
            ) : (
              <PopoverButton className='input mb-5 mt-0.5 flex min-h-[46px] w-full min-w-56 cursor-pointer items-center rounded-md border border-grey-20 px-3 py-2 focus-visible:border-grey-40 focus-visible:outline-none'>
                <div className='flex flex-1 flex-wrap gap-1 text-left'>
                  {value.length > 0 ? (
                    <TagsBuilder tags={value} onRemove={onRemove} />
                  ) : (
                    <span className='text-grey-40'>None Selected</span>
                  )}
                </div>
                <ChevronUpDownIcon
                  className='h-5 w-5 text-gray-400'
                  aria-hidden='true'
                />
              </PopoverButton>
            )}

            <PopoverStateChangedEffect
              open={open}
              onClose={() => setIsOpen(false)}
              onOpenChange={setIsOpen}
            />

            <MenuTransition>
              <PopoverPanel
                ref={refs.setFloating}
                style={floatingStyles}
                className={classNames(
                  'z-50 flex flex-col overflow-y-auto rounded-md border border-grey-20 bg-white text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none focus-visible:border-grey-40 focus-visible:outline-none sm:text-sm',
                  containerStyles
                )}
              >
                <TagCombolist
                  items={options}
                  searchKeys={['name', 'category', 'description']}
                  placeholder='tags'
                  value={value}
                  onChange={onChange}
                  onCreate={onCreate}
                  onDelete={onDelete}
                />

                <div className='sticky bottom-0 z-10 flex justify-between rounded-b-md border-t bg-white p-1.5 text-xs'>
                  <PopoverButton
                    className='p-2 text-left uppercase text-grey-40 hover:text-brand'
                    onClick={() => onModalOpen && onModalOpen(false)}
                  >
                    Cancel
                  </PopoverButton>
                  <PopoverButton className='w-16 pr-2 text-right uppercase text-grey-40 hover:text-brand'>
                    Ok
                  </PopoverButton>
                </div>
              </PopoverPanel>
            </MenuTransition>
          </>
        )}
      </Popover>
    </>
  );
}

function DialogMode({
  options,
  containerStyles,
  value,
  hasPreview = true,
  onCloseDialog,
  onChange,
  onCreate,
  onDelete,
  onRemove,
  onSubmit,
}: ModesProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const { isOverflowing } = useCheckOverflow(dialogRef);

  return (
    <div
      ref={dialogRef}
      className={classNames(
        'max-h-96 rounded-md border border-grey-20 bg-white shadow-lg',
        isOverflowing ? 'overflow-auto' : 'overflow-visible',
        containerStyles
      )}
    >
      {hasPreview && (
        <Transition
          as='div'
          show={value.length > 0}
          enter='transition-opacity duration-200'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='transition-opacity duration-150'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='mx-2 mt-2'>
            <span className='mb-1.5 font-medium text-grey-50'>
              Selected Tags
            </span>
            <div className='flex min-h-[32px] flex-wrap items-center gap-1 rounded border border-grey-20 p-1 text-base'>
              <TagsBuilder
                tags={value}
                onRemove={onRemove}
                limit={10}
                className='!gap-1'
              />
            </div>
          </div>
        </Transition>
      )}

      <TagCombolist
        items={options}
        searchKeys={['name', 'category', 'description']}
        placeholder='tags'
        value={value}
        onChange={onChange}
        onCreate={onCreate}
        onDelete={onDelete}
      />

      <div className='sticky bottom-0 z-10 flex justify-between rounded-b-md border-t bg-white p-1.5 text-xs'>
        <button
          className='p-2 text-left uppercase text-grey-40 hover:text-brand'
          onClick={onCloseDialog}
        >
          Cancel
        </button>
        <button
          className='w-16 pr-2 text-right uppercase text-grey-40 hover:text-brand'
          onClick={(e) => {
            e.preventDefault();
            onSubmit?.();
            onCloseDialog?.();
          }}
        >
          Ok
        </button>
      </div>
    </div>
  );
}
